import { ContentfulPageProps } from "../types/vino-data-types";
import { Layout } from "../components/layout";
import { PageHeader } from "../components/page-header";
import PageSection from "../component-factory/page-section";
import { useMemo, useState, useEffect } from "react";
import SEO from "../components/seo/seo";
import SearchProvider from "../contexts/search/provider";
import { getPageSeoTitle } from "../utils/get-seo-title";
import useAuth from "../hooks/use-auth";
import { ProductPreloader } from "../components/preloaders/ProductPreloader";
import config from "../../config.json";
import { VinoUser } from "../contexts/auth/state";
import { determineTierIndex, isUserAStaff } from "../utils/loyalty";
import PayWall from "../components/pay-wall/PayWall";
import { LockIcon } from "../components/pay-wall/Icons";
import isBrowser from "../utils/is-browser";

const ContentfulPage: React.FC<ContentfulPageProps> = ({
  pageContext,
}): JSX.Element => {
  const {
    user,
    isLoading: authLoading,
    validateEmail,
    emailExistInAdmin,
  } = useAuth();
  const { id, page, pagePath } = pageContext;
  const {
    title,
    pageSections,
    noIndex,
    seoTitle,
    seoDescription,
    route,
    heroImage,
    internalName,
    tiersEligible,
  } = page;

  const [isEligible, setIsEligible] = useState(
    !tiersEligible || tiersEligible?.includes("ALL")
  );
  const [modal, setModal] = useState<any>(<ProductPreloader />);
  const BIGCOMMERCE_CUSTOMER_KEY = `BIGCOMMERCE_${process.env.GATSBY_VM_MARKET}_CUSTOMER_ID`;
  const { tier } = determineTierIndex(
    user as VinoUser,
    true,
    user?.customer_group?.name || "GREEN"
  );
  const tierMap = config[process.env.GATSBY_VM_MARKET]["TIER_MAP"] || {};
  const isCustomerEligible = useMemo(
    () =>
      Array.isArray(tiersEligible) &&
      tiersEligible.some(
        (pageTier) =>
          tierMap[tier === "none" ? "GREEN" : tier?.toUpperCase() || "GREEN"] >=
          tierMap[pageTier.toUpperCase()]
      ),
    [tiersEligible, tier]
  );

  const emailParam = isBrowser()
    ? decodeURIComponent(
        new URLSearchParams(window.location.search).get("email") || ""
      ).replace(/ /g, "+")
    : "";

  useEffect(() => {
    const checkEligibility = async () => {
      const staffStatus = user ? isUserAStaff(user) : null;

      // Don't run checks until auth is done
      if (authLoading) return;

      // Wait until staff status is determined (avoids flicker)
      if (staffStatus === undefined) {
        if (
          user?.["https://www.vinomofo.com/app_metadata"][BIGCOMMERCE_CUSTOMER_KEY]
        ) {
          return;
        }
      }

      // 1. If ALL tiers are allowed, skip all checks
      if (!tiersEligible || tiersEligible.includes("ALL")) {
        setIsEligible(true);
        return;
      }

      // 2. Staff bypasses all gating
      if (staffStatus === true) {
        setIsEligible(true);
        return;
      }

      // 3. Guest gating: check if user is not logged in
      if (!user) {
        // 3a. Validate email if present
        if (emailParam) {
          try {
            const existInAuth0 = await validateEmail(emailParam);
            const existInAdmin = existInAuth0
              ? true
              : await emailExistInAdmin(emailParam);

            if (!existInAuth0 && !existInAdmin) {
              setModal(
                <PayWall
                  title="Create your password"
                  LoginButton={false}
                  isSignUp={true}
                  cause="email_not_valid"
                  theme="modal"
                  category="Spirit"
                  page_tier={tiersEligible.toString()}
                  user_tier={tier?.toUpperCase()}
                  userEmail={emailParam}
                />
              );
              setIsEligible(false);
              return;
            }
          } catch (err) {
            console.warn("Email validation failed:", err);
          }
        }

        // 3b. Handle guest access restriction
        if (!tiersEligible.includes("GUEST")) {
          setModal(
            <PayWall
              title={
                <>
                  <LockIcon
                    width="30"
                    height="28"
                    style={{ marginBottom: "-2px" }}
                  />
                  <span>
                    Login to see all of our exclusive Whiskymofo deals.
                  </span>
                </>
              }
              LoginButton={true}
              cause="guest_cannot_see"
              theme="modal"
              category="Spirit"
              page_tier={tiersEligible.toString()}
              user_tier={"GUEST"}
              userEmail={emailParam}
            />
          );
          setIsEligible(false);
          return;
        }

        // 3c. If guest is allowed
        setIsEligible(true);
        return;
      }

      // 4. If user is not eligible by tier
      if (staffStatus === false && !isCustomerEligible) {
        setModal(
          <PayWall
            title="This is awkward... You'll need to make a purchase to unlock exclusive deals. Shop now to see what you're missing!"
            LoginButton={false}
            isSignUp={false}
            cause="tier_too_low"
            theme="modal"
            category="Spirit"
            page_tier={tiersEligible.toString()}
            user_tier={user?.customer_group?.name?.toUpperCase()}
            userEmail={user?.email || emailParam}
          />
        );
        setIsEligible(false);
        return;
      }

      // 5. Default: eligible
      setIsEligible(true);
    };

    checkEligibility();
  }, [
    user,
    authLoading,
    isCustomerEligible,
    tiersEligible,
    emailParam,
    validateEmail,
    emailExistInAdmin,
  ]);

  if (!isEligible) {
    return (
      <Layout>
        <SEO
          title={seoTitle}
          type={"event"}
          image={heroImage?.url || page?.backgroundImage?.url || ""}
          pathname={route?.path || ""}
          description={seoDescription || ""}
          robots={noIndex ? "noindex, nofollow" : "index, follow"}
        />
        {modal}
      </Layout>
    );
  }

  return (
    <Layout
      bgImage={page?.backgroundImage?.url}
      withSearchBox={/\/(spirits|wines)\//.test(pagePath)}
    >
      <SEO
        title={getPageSeoTitle(title, seoTitle, internalName)}
        description={seoDescription}
        type={"page"}
        pathname={pagePath}
        robots={noIndex ? "noindex, nofollow" : "index, follow"}
      />
      <SearchProvider>
        <PageHeader {...page} pagePath={pagePath} />
        {pageSections.map((section, index) => {
          return PageSection.create({ ...section, pageId: id }, index);
        })}
      </SearchProvider>
    </Layout>
  );
};

export default ContentfulPage;
